import React, { useEffect, useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// === CONFIG (purple) ===
const COLORS = {
  Service: "#9848c7", // primary
  Inne: "#cdb0ea",    // lighter purple
};

const monthsList = [
  "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
  "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"
];

const parseValue = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const formatNumber = (value) =>
  typeof value === "number"
    ? value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    : value;

// === STYLES ===
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

// === COMPONENT ===
const ZakupyTypesByCountry = ({ zakupyData = [] }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("00");

  useEffect(() => {
    const years = [...new Set(zakupyData.map((row) => row.Rok?.toString()))]
      .filter(Boolean)
      .sort();
    setAvailableYears(years);

    if (!selectedYear && years.length > 0) {
      setSelectedYear(years.includes("2024") ? "2024" : years[0]);
    }
  }, [zakupyData, selectedYear]);

  const chartData = useMemo(() => {
    const countryTotals = {};

    const filtered = zakupyData.filter(
      (row) =>
        row.Rok?.toString() === selectedYear &&
        (selectedMonth === "00" || parseInt(row.Miesiac) === parseInt(selectedMonth))
    );

    filtered.forEach((row) => {
      const country = (row.KodKrajuNadaniaTIN || "PL").trim().toUpperCase();
      if (!country) return;

      if (!countryTotals[country]) {
        countryTotals[country] = { Service: 0, Inne: 0 };
      }

      const net = parseValue(row.K_42);
      const vat = parseValue(row.K_43);
      const total = net + vat;

      // Heuristic: 15% as services, rest as "Inne"
      const serviceAmount = total * 0.15;
      const inneAmount = Math.max(0, total - serviceAmount);

      countryTotals[country].Service += serviceAmount;
      countryTotals[country].Inne += inneAmount;
    });

    const labels = Object.keys(countryTotals);

    return {
      labels,
      datasets: [
        {
          label: "Usługi",
          data: labels.map((c) => countryTotals[c].Service),
          backgroundColor: COLORS.Service,
          stack: "split",
        },
        {
          label: "Inne",
          data: labels.map((c) => countryTotals[c].Inne),
          backgroundColor: COLORS.Inne,
          stack: "split",
        },
      ],
    };
  }, [zakupyData, selectedYear, selectedMonth]);

  return (
    <div style={{ width: "100%", margin: "20px auto", textAlign: "center" }}>
      <ChartTitle>
        Podział zakupów: Usługi vs Inne (kraje)
        <InfoIconWrapper data-tooltip-id="types-country-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="types-country-tooltip"
        place="right"
        style={{
          fontSize: "12px",
          fontFamily: "Arial, sans-serif",
          color: "#fff",
        }}
        content={
          <>
            <strong>Usługi vs Inne</strong><br />
            Usługi = 15% wartości zakupu (heurystyka).<br />
            Inne = pozostałe 85%.<br />
            Podział wg krajów.
          </>
        }
      />

      <div style={{
        marginBottom: "20px",
        display: "flex",
        justifyContent: "center",
        gap: "12px",
        alignItems: "center"
      }}>
        <label htmlFor="year" style={{ fontWeight: 500 }}>Rok:</label>
        <select
          id="year"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          style={{
            padding: "8px 12px",
            fontSize: "15px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          {availableYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>

        <label htmlFor="month" style={{ fontWeight: 500 }}>Miesiąc:</label>
        <select
          id="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{
            padding: "8px 12px",
            fontSize: "15px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          <option value="00">Cały rok</option>
          {monthsList.map((m, idx) => (
            <option key={idx + 1} value={idx + 1}>{m}</option>
          ))}
        </select>
      </div>

      <div style={{ width: "100%", maxWidth: "1100px", height: "600px", margin: "0 auto" }}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: "bottom" },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  title: () => "",
                  label: (context) =>
                    `${context.dataset.label}: ${formatNumber(context.raw)}`
                },
              },
            },
            scales: {
              x: { stacked: true },
              y: {
                stacked: true,
                title: {
                  display: true,
                  text: "Kwota",
                  color: "#666",
                  font: { size: 12, weight: "bold" },
                },
                ticks: {
                  callback: (value) => formatNumber(value),
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default ZakupyTypesByCountry;
  